import { Alert, Box, Column, SectionHeading } from "@hightouch/htui";
import * as Sentry from "src/lib/monitoring";
import { HeatmapDataSchema } from "@hightouch/lib/llm/schema/components/schemas";
import { transformHeatmapToGraphSeries } from "./heatmap-transformer";
import { Card } from "src/components/card";
import { type ReactNode, useRef } from "react";
import { HeatmapTable } from "src/pages/analytics/heatmap-table";
import {
  parseJsonChildren,
  DEFAULT_VERTICAL_CHART_HEIGHT,
  COMPACT_VERTICAL_CHART_HEIGHT,
} from "./chart-utils";

/**
 * Heatmap component for displaying multi-dimensional performance data
 * from LLM agents. Uses the existing HeatmapTable component via AnalyticsGraph.
 *
 * Data is passed as children content (JSON string).
 */
export const Heatmap = ({
  children,
  compact = false,
}: {
  children: ReactNode;
  compact?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  try {
    const heatmapData = parseJsonChildren(children, HeatmapDataSchema);
    const graphSeries = transformHeatmapToGraphSeries(heatmapData);

    const table = (
      <Column
        width="100%"
        maxHeight={
          compact
            ? COMPACT_VERTICAL_CHART_HEIGHT
            : DEFAULT_VERTICAL_CHART_HEIGHT
        }
        overflow="auto"
      >
        <HeatmapTable
          data={graphSeries}
          showActionsAndLegend={false}
          compact={true}
          tooltipContainerRef={containerRef}
        />
      </Column>
    );

    if (compact) {
      return (
        <Box position="relative" zIndex={0} ref={containerRef}>
          {table}
        </Box>
      );
    }

    return (
      <Box my={4} position="relative" zIndex={0} ref={containerRef}>
        <Card p={4}>
          <Column gap={2}>
            <SectionHeading>{heatmapData.config.title}</SectionHeading>
            {table}
          </Column>
        </Card>
      </Box>
    );
  } catch (error) {
    Sentry.captureException(error);
    return (
      <Alert
        type="error"
        title="Chart Error"
        message="Unable to render heatmap, invalid data."
        my={4}
      />
    );
  }
};
